import { Button, FormControl, IdentityVerificationNeededIllu, Input, Modal } from '@farol-ds/react'
import { sendMetrics } from '@jusbrasil-web/shared-utils-events'
import { formatDate } from '@jusbrasil-web/shared-utils-format'
import { validateBirthDate, validateCpf } from '@jusbrasil-web/shared-utils-validators'
import { ChangeEvent, FormEvent, KeyboardEvent, useEffect, useRef, useState } from 'react'
import { useIMask } from 'react-imask'

import { useGA4Events } from '../../../hooks'
import { useModalContext } from '../../cpf-validation/context'
import { Steps } from '../../cpf-validation/enums'
import styles from './complete-signup-modal.module.scss'
import {
  NUDGE_CLICK_PROPS,
  NUDGE_INPUT_START_PROPS,
  NUDGE_INPUT_SUBMIT_PROPS,
  NUDGE_RENDER_PROPS,
} from './ga4-events'

// Same validations/copy as the signup CPF form (auth-core can't be imported from shared).
const MIN_AGE = 16
const CPF_REQUIRED = 'Digite seu CPF'
const CPF_INVALID = 'CPF inválido'
const BIRTH_DATE_REQUIRED = 'Digite sua data de nascimento'
const BIRTH_DATE_INVALID = 'Data inválida'
const BIRTH_DATE_BELOW_MIN_AGE = 'Idade mínima de 16 anos.'

function birthDateToIso(masked: string): string {
  return masked.split('/').reverse().join('-')
}

function isBelowMinimumAge(iso: string): boolean {
  const [year, month, day] = iso.split('-').map(Number)
  const now = new Date()
  const todayUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  return Date.UTC(year + MIN_AGE, month - 1, day) > todayUTC
}

function getCpfError(unmaskedCpf: string): string | undefined {
  const digits = (unmaskedCpf || '').replace(/\D/g, '')
  if (digits.length === 0) return CPF_REQUIRED
  return digits.length === 11 && validateCpf(digits) ? undefined : CPF_INVALID
}

function getBirthDateError(masked: string): string | undefined {
  const digits = (masked || '').replace(/\D/g, '')
  if (digits.length === 0) return BIRTH_DATE_REQUIRED
  const iso = birthDateToIso(masked)
  if (!validateBirthDate(iso)) return BIRTH_DATE_INVALID
  if (isBelowMinimumAge(iso)) return BIRTH_DATE_BELOW_MIN_AGE
  return undefined
}

interface CpfDobStepProps {
  firstName?: string | null
  onSkip: () => void
}

export function CpfDobStep({ firstName, onSkip }: CpfDobStepProps) {
  const { setCurrentStep, setSharedData, sharedData, viewer, eventData } = useModalContext()
  const { cpf, birthDate } = viewer || {}
  const pid = viewer?.pid.toString()
  const ga4 = useGA4Events()
  const formRef = useRef<HTMLFormElement>(null)
  const submitButtonRef = useRef<HTMLButtonElement>(null)
  const inputStartedRef = useRef(false)
  const {
    ref: cpfRef,
    unmaskedValue: unmaskedCpf,
    maskRef,
  } = useIMask(useRef({ mask: '000.000.000-00', lazy: false }).current)
  const { ref: birthDateRef, maskRef: birthDateMaskRef } = useIMask(
    useRef({ mask: '00/00/0000', lazy: false }).current
  )
  const [errors, setErrors] = useState<{ cpf?: string; birthDate?: string }>({})

  useEffect(() => {
    ga4.sendComponentRender(NUDGE_RENDER_PROPS.CPF_DOB)
    sendMetrics('UserAccount.IdValidationViewed', pid, { ...eventData })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Prefill through the imask instances (not the DOM input): setting the element value
  // directly would wipe the lazy:false placeholder mask until the field is focused.
  useEffect(() => {
    if (maskRef.current) {
      maskRef.current.value = sharedData.cpf || cpf || ''
    }
    if (birthDateMaskRef.current) {
      const defaultValue = sharedData.birthDate?.getTime() || birthDate
      birthDateMaskRef.current.value = defaultValue ? formatDate(defaultValue) : ''
    }
  }, [maskRef, birthDateMaskRef, sharedData, cpf, birthDate])

  const title = firstName
    ? `${firstName}, precisamos de informações suas.`
    : 'Precisamos de informações suas.'

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    if (!inputStartedRef.current && e.target.value) {
      ga4.sendInputStart(NUDGE_INPUT_START_PROPS.CPF_DOB)
      inputStartedRef.current = true
    }
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Enter') return
    e.preventDefault()
    submitButtonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    submitButtonRef.current?.focus()
  }

  function handleCpfBlur() {
    const complete = (unmaskedCpf || '').replace(/\D/g, '').length === 11
    setErrors((prev) => ({ ...prev, cpf: complete ? getCpfError(unmaskedCpf) : undefined }))
  }

  function handleBirthDateBlur() {
    const value = birthDateRef.current?.value || ''
    const complete = value.replace(/\D/g, '').length === 8
    setErrors((prev) => ({ ...prev, birthDate: complete ? getBirthDateError(value) : undefined }))
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    setErrors({})
    const birthDateMasked = birthDateRef.current?.value || ''
    const cpfError = getCpfError(unmaskedCpf)
    const birthDateError = getBirthDateError(birthDateMasked)
    if (cpfError || birthDateError) {
      setErrors({ cpf: cpfError, birthDate: birthDateError })
      return
    }
    const birthDateObject = new Date(`${birthDateToIso(birthDateMasked)}T00:00:00.000-03:00`)
    ga4.sendInputSubmitComponent(NUDGE_INPUT_SUBMIT_PROPS.CPF_DOB)
    sendMetrics('UserAccount.IdValidationSubmitted', pid, {
      ...eventData,
      metadata: {
        ...eventData?.metadata,
        cpf: unmaskedCpf,
        birthDate: formatDate(birthDateObject.getTime()),
      },
    })
    setSharedData({ ...sharedData, cpf: unmaskedCpf, birthDate: birthDateObject })
    setCurrentStep(Steps.VALIDATING_IDENTITY)
  }

  function onSkipClick() {
    ga4.sendClick(NUDGE_CLICK_PROPS.CPF_DOB_SKIP)
    onSkip()
  }

  return (
    <>
      <Modal.Body data-testid="complete-signup-cpf-dob-step">
        <div className={`${styles.illustratedHeader} ${styles.cpfDobSpacing}`}>
          <IdentityVerificationNeededIllu size="sm" />
          <Modal.HeaderTitle>{title}</Modal.HeaderTitle>
          <Modal.HeaderDescription>
            <p>Em breve, o Jusbrasil não permitirá contas que não tenham esses dados:</p>
          </Modal.HeaderDescription>
        </div>
        <form ref={formRef} className={styles.form} onSubmit={onSubmit} noValidate>
          <FormControl error={!!errors.cpf}>
            <FormControl.Label>CPF</FormControl.Label>
            <FormControl.Description>Para consultar seus processos</FormControl.Description>
            <Input
              name="cpf"
              inputMode="numeric"
              ref={cpfRef}
              onChange={handleChange}
              onBlur={handleCpfBlur}
            />
            <FormControl.ErrorMessage>{errors.cpf}</FormControl.ErrorMessage>
          </FormControl>
          <FormControl error={!!errors.birthDate}>
            <FormControl.Label>Data de nascimento</FormControl.Label>
            <FormControl.Description>
              Ajuda a confirmar que a consulta é sobre você
            </FormControl.Description>
            <Input
              type="text"
              inputMode="numeric"
              ref={birthDateRef}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onBlur={handleBirthDateBlur}
            />
            <FormControl.ErrorMessage>{errors.birthDate}</FormControl.ErrorMessage>
          </FormControl>
        </form>
      </Modal.Body>
      <Modal.Footer className={styles.footer}>
        <Button
          kind="primary"
          ref={submitButtonRef}
          onClick={() => formRef.current?.requestSubmit()}
        >
          Confirmar dados
        </Button>
        <Button kind="plain" onClick={onSkipClick}>
          Preencher depois
        </Button>
      </Modal.Footer>
    </>
  )
}
